import { ArrowLeft, Flame, TrendingUp } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { useState, useEffect } from "react";
import welcomeBg from "../../assets/homebg.png";
import { get } from "@/services/axiosUitils";
import { CHART_AXIS_TICK_LG, CHART_FONT_SIZES } from "../../utils/chartConfig";

const CaloriesBurnedDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("weekly");

  // Get selected date from FitnessPage navigation
  const selectedDate = location.state?.selectedDate || null;

  // API data states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [weeklyData, setWeeklyData] = useState<any[]>([]);
  const [monthlyData, setMonthlyData] = useState<any[]>([]);
  const [summaryData, setSummaryData] = useState({
    calories: 0,
    total_calories: 0,
    min_calories: 0,
    avg_calories: 0,
    max_calories: 0,
    days: 7,
  });
  const [dateRange, setDateRange] = useState({
    start_date: "",
    end_date: "",
  });

  // Fetch calories data from API with selected date
  const fetchCaloriesData = async (type: "weekly" | "monthly") => {
    try {
      setLoading(true);
      setError(null);

      // Build API URL with date parameter if available
      const dateParam = selectedDate ? `&date=${selectedDate}` : "";
      const apiUrl = `fitbit_scan/calories-graph/?type=${type}${dateParam}`;

      const response = await get(apiUrl);

      // Handle both boolean and object success formats
      const isSuccess =
        typeof response.success === "boolean"
          ? response.success
          : response.success?.code === "Retrieved";

      if (isSuccess && response.summary) {
        setSummaryData(response.summary);
        setDateRange({
          start_date: response.start_date,
          end_date: response.end_date,
        });

        if (type === "weekly") {
          // Transform weekly API data to chart format
          const chartData = response.graph.map((item: any) => ({
            day: item.day,
            calories: item.value,
          }));
          setWeeklyData(chartData);
        } else {
          // Transform monthly API data to chart format
          const chartData = response.graph.map((item: any, index: number) => ({
            week: `Week ${index + 1}`,
            calories: item.value,
          }));
          setMonthlyData(chartData);
        }
      }
    } catch (err: any) {
      setError("Failed to fetch calories data");
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change and fetch data
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab === "weekly") {
      fetchCaloriesData("weekly");
    } else if (tab === "monthly") {
      fetchCaloriesData("monthly");
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchCaloriesData("weekly");
  }, []);

  // Format date range for display
  const formatDateRange = () => {
    if (!dateRange.start_date || !dateRange.end_date) {
      return "";
    }

    const startDate = new Date(dateRange.start_date).toLocaleDateString(
      "en-US",
      {
        month: "short",
        day: "numeric",
        year: "numeric",
      }
    );
    const endDate = new Date(dateRange.end_date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    return `${startDate} - ${endDate}`;
  };

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() =>
              navigate("/glucera/fitness", {
                state: {
                  selectedDate: selectedDate,
                  selectedDateObj: location.state?.selectedDateObj,
                },
              })
            }
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="text-center">
            <h1 className="text-title3 font-bold text-foreground">
              Calories Burned
            </h1>
            {selectedDate ? (
              <p className="text-caption1 text-muted-foreground">
                {activeTab === "weekly" ? "Week of" : "Month of"}{" "}
                {new Date(selectedDate + "T00:00:00").toLocaleDateString()}
              </p>
            ) : (
              <p className="text-caption1 text-muted-foreground">
                Track your energy expenditure
              </p>
            )}
          </div>
          <div className="w-10" />
        </div>

        {/* Today's Summary */}
        <Card className="rounded-2xl border border-border bg-gradient-to-br from-orange-50 to-orange-100 p-5 shadow-lg dark:from-orange-950 dark:to-orange-900">
          <div className="mb-4 flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-orange-500/20">
              <Flame className="h-7 w-7 text-orange-500" />
            </div>
            <div className="flex-1">
              <div className="text-large-title font-bold text-foreground">
                {loading
                  ? "..."
                  : (
                      summaryData?.calories ||
                      summaryData?.total_calories ||
                      0
                    ).toLocaleString()}
              </div>
              <p className="text-footnote text-muted-foreground">
                kcal burned{" "}
                {activeTab === "weekly" ? "this week" : "this month"}
              </p>
              {dateRange.start_date && (
                <p className="mt-1 text-caption1 text-muted-foreground">
                  {formatDateRange()}
                </p>
              )}
            </div>
          </div>
        </Card>

        {/* Info Card */}
        <Card className="rounded-2xl border border-border bg-orange-50/30 p-4 shadow-lg dark:bg-orange-950/30">
          <div className="flex gap-3">
            <TrendingUp className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-500" />
            <div>
              <h4 className="mb-1 text-headline font-semibold text-foreground">
                About Calories Burned
              </h4>
              <p className="text-callout leading-relaxed text-muted-foreground">
                Calories burned includes basal metabolic rate, activity
                calories, and exercise. Fitbit estimates this using your heart
                rate, movement patterns, and personal metrics like age, weight,
                and height.
              </p>
            </div>
          </div>
        </Card>

        {/* Tabs for Weekly/Monthly */}
        <Card className="rounded-2xl border border-border bg-card shadow-lg">
          <div className="p-4">
            <div className="inline-flex w-full max-w-sm rounded-xl bg-muted p-1">
              <button
                onClick={() => handleTabChange("weekly")}
                className={`flex-1 rounded-lg px-4 py-2.5 text-subhead font-medium transition-all duration-200 ${
                  activeTab === "weekly"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-background/50 hover:text-foreground"
                }`}
              >
                Weekly
              </button>
              <button
                onClick={() => handleTabChange("monthly")}
                className={`flex-1 rounded-lg px-4 py-2.5 text-subhead font-medium transition-all duration-200 ${
                  activeTab === "monthly"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-background/50 hover:text-foreground"
                }`}
              >
                Monthly
              </button>
            </div>
          </div>

          <div className="space-y-4 px-4 pb-4 pt-0">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="mx-auto mb-2 h-8 w-8 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
                  <p className="text-footnote text-muted-foreground">
                    Loading calories data...
                  </p>
                </div>
              </div>
            )}

            {error && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <p className="mb-2 text-footnote text-red-600">{error}</p>
                  <button
                    onClick={() =>
                      fetchCaloriesData(activeTab as "weekly" | "monthly")
                    }
                    className="text-footnote text-orange-500 hover:text-orange-600"
                  >
                    Try again
                  </button>
                </div>
              </div>
            )}

            {!loading && !error && activeTab === "weekly" && (
              <div>
                <h3 className="mb-4 font-semibold text-foreground">
                  This Week's Calories
                  {dateRange.start_date && (
                    <span className="ml-2 text-footnote font-normal text-muted-foreground">
                      ({formatDateRange()})
                    </span>
                  )}
                </h3>
                <div className="h-64 overflow-hidden">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={weeklyData}
                      margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="day" stroke="#6b7280" tick={CHART_AXIS_TICK_LG} />
                      <YAxis stroke="#6b7280" tick={CHART_AXIS_TICK_LG} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "2px solid hsl(var(--border))",
                          borderRadius: "12px",
                          color: "hsl(var(--foreground))",
                          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.2)",
                        }}
                        itemStyle={{ color: "hsl(var(--foreground))" }}
                        labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                        formatter={(value: any) => [
                          `${(Number(value) || 0).toLocaleString()} kcal`,
                          "Calories",
                        ]}
                      />
                      <Bar
                        dataKey="calories"
                        fill="#f97316"
                        radius={[8, 8, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {!loading && !error && activeTab === "monthly" && (
              <div>
                <h3 className="mb-4 font-semibold text-foreground">
                  This Month's Calories
                  {dateRange.start_date && (
                    <span className="ml-2 text-footnote font-normal text-muted-foreground">
                      ({formatDateRange()})
                    </span>
                  )}
                </h3>
                <div className="h-64 overflow-hidden">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={monthlyData}
                      margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="week" stroke="#6b7280" tick={CHART_AXIS_TICK_LG} />
                      <YAxis stroke="#6b7280" tick={CHART_AXIS_TICK_LG} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "2px solid hsl(var(--border))",
                          borderRadius: "12px",
                          color: "hsl(var(--foreground))",
                          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.2)",
                        }}
                        itemStyle={{ color: "hsl(var(--foreground))" }}
                        labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                        formatter={(value: any) => [
                          `${(Number(value) || 0).toLocaleString()} kcal`,
                          "Calories",
                        ]}
                      />
                      <Bar
                        dataKey="calories"
                        fill="#f97316"
                        radius={[8, 8, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CaloriesBurnedDetail;
